import { VirtualItem, Virtualizer } from "@tanstack/react-virtual";
import { RecordingsState, SegmentItem } from "./InsightsReportsTypes";
import { InsightsSegment } from "@/types/insightsApi";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, Download, Headphones, Info, Loader2, PhoneIncoming, PhoneOutgoing } from "lucide-react";
import { buildFilename, downloadSingle } from "@/helpers/downloadRecordings";
import { Button } from "../ui/button";
import React, { useEffect, useMemo } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { getRecordingUrl } from "@/helpers/insightsRecordingUrl";
import { format } from "date-fns/format";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";

type InsightsVirtualRowProps = {
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  virtualItem: VirtualItem;
  segmentItem: SegmentItem;
  recordingsCache: Map<string, RecordingsState>;
  toggleRow: (record: InsightsSegment) => void;
  toggleSelect: (segmentSid: string) => void;
  isExpanded: boolean;
  isSelected: boolean;
  gridColsStyle: string;
  cellStyle: string;
  baseRowHeight: number;
};

function DirectionBadge({ direction }: { direction: "Inbound" | "Outbound" }) {
  return (
    <Badge variant="outline" className={cn("gap-1", direction === "Inbound" ? "text-blue-600" : "text-orange-600")}>
      {direction === "Inbound" ? <PhoneIncoming className="size-3" /> : <PhoneOutgoing className="size-3" />}
      {direction}
    </Badge>
  );
}

function formatSeconds(raw: string): string {
  if (!raw) return "—";
  const secs = Math.round(parseFloat(raw));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

type RowItemProps = Omit<InsightsVirtualRowProps, "virtualizer" | "virtualItem" | "recordingsCache">;

function RowItem({
  segmentItem,
  gridColsStyle,
  cellStyle,
  toggleRow,
  toggleSelect,
  isExpanded,
  isSelected,
  baseRowHeight,
}: RowItemProps) {
  const { record } = segmentItem;

  const formattedDate = useMemo(() => {
    return format(new Date(record.date), "yyyy-MM-dd");
  }, [record.date]);

  return (
    <div
      key={record.segment}
      onClick={() => toggleRow(record)}
      className={cn(
        "hover:bg-muted/40 left-0 grid w-full cursor-pointer border-b transition-colors",
        isExpanded && "bg-muted/40",
        isSelected && "bg-primary/5"
      )}
      style={{ height: baseRowHeight, gridTemplateColumns: gridColsStyle }}
    >
      <div className="flex items-center justify-center px-3">
        <Checkbox
          checked={isSelected}
          aria-label="Select row"
          onCheckedChange={() => toggleSelect(record.segment)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <div className={cellStyle}>
        <DirectionBadge direction={record.direction} />
      </div>
      <div className={cn(cellStyle, "font-mono text-xs")}>{record.customerPhone}</div>
      <div className={cellStyle}>{formattedDate}</div>
      <div className={cellStyle}>{record.time}</div>
      <div className={cellStyle}>{record.agent}</div>
      <div className={cellStyle}>{record.queue}</div>
      <div className={cellStyle}>{record.department || <span className="text-muted-foreground">—</span>}</div>
      <div className={cn(cellStyle, "tabular-nums")}>{formatSeconds(record.totalWaitingTime)}</div>
      <div className={cn(cellStyle, "tabular-nums")}>{formatSeconds(record.totalTalkTime)}</div>
      <div className="flex items-center justify-end gap-1 pr-3">
        <ChevronDown className={cn("text-muted-foreground size-4 transition-transform", isExpanded && "rotate-180")} />
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`Copy task ID ${record.segment}`}
              className="hover:bg-muted rounded p-1"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
                  await navigator.clipboard.writeText(record.segment);
                  toast.success("Task ID copied");
                } catch {
                  toast.error("Failed to copy Task ID");
                }
              }}
            >
              <Info className="text-muted-foreground size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            Task ID: <span className="font-mono">{record.segment}</span>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

type ExpandedRowProps = Pick<InsightsVirtualRowProps, "virtualizer" | "segmentItem" | "recordingsCache">;

function ExpandedRow({ virtualizer, segmentItem, recordingsCache }: ExpandedRowProps) {
  const { record } = segmentItem;
  const recState = recordingsCache.get(record.segment);
  const recordings = Array.isArray(recState) ? recState : [];
  const recLoading = recState === "loading";
  const recError = recState === "error";

  useEffect(() => {
    virtualizer.measure();
  }, [virtualizer]);

  return (
    <div key={`expanded-${record.segment}`} className="bg-muted/40 left-0 w-full border-b px-6 pt-2 pb-4">
      <p className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">Recordings</p>

      {recLoading && (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" />
          Loading recordings…
        </div>
      )}

      {recError && <p className="text-destructive text-sm">Failed to load recordings. Please try again.</p>}

      {!recLoading && !recError && recordings.length === 0 && (
        <p className="text-muted-foreground text-sm">No recordings found.</p>
      )}

      <div className="flex flex-col gap-2">
        {recordings.map((rec, idx) => {
          const filename = buildFilename(record.date, record.time, rec.sid);
          return (
            <div key={rec.sid} className="bg-background flex items-center gap-3 rounded-md border px-4 py-2.5">
              <Headphones className="text-muted-foreground size-4 shrink-0" />
              <span className="text-sm font-medium">Recording {idx + 1}</span>
              <span className="text-muted-foreground text-xs">{formatDuration(rec.duration)}</span>
              <audio className="flex-1" controls preload="metadata">
                <source src={getRecordingUrl(rec)} type="audio/mp3" />
                Unfortunately your browser does not support the audio element.
              </audio>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={async (e) => {
                  e.stopPropagation();
                  const res = await fetch(getRecordingUrl(rec));
                  const buf = await res.arrayBuffer();
                  downloadSingle({ filename, data: new Uint8Array(buf) });
                }}
              >
                <Download className="size-3.5" />
                Download
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InsightsVirtualRow(props: InsightsVirtualRowProps) {
  const { isExpanded, toggleRow, segmentItem, virtualItem, baseRowHeight } = props;

  return (
    <Collapsible
      key={segmentItem.record.segment}
      open={isExpanded}
      className="absolute left-0 w-full"
      onOpenChange={() => toggleRow(segmentItem.record)}
      style={{ top: virtualItem.start, height: virtualItem.size }}
    >
      <CollapsibleTrigger style={{ height: baseRowHeight }} className="w-full" asChild>
        <RowItem {...props} baseRowHeight={baseRowHeight} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ExpandedRow {...props} />
      </CollapsibleContent>
    </Collapsible>
  );
}

export const MemoizedInsightsVirtualRow = React.memo(InsightsVirtualRow);
